import { TranslateButton } from "@/components/TranslateButton";
import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Clock, Sun, Moon } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { NAV_ITEMS } from "@/components/navConfig";
import { MemberChip } from "@/components/MemberChip";
import logo from "@/assets/bgrmatka-logo.png";

function IstClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const date = now.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
  const time = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
  return (
    <div data-testid="ist-clock-strip" className="border-b-2 border-[#7A1020] bg-gradient-to-r from-[#F3C24B] via-[#FFD700] to-[#F3C24B]">
      <div className="mx-auto flex max-w-6xl items-center justify-center gap-2 px-3 py-1.5 text-[#20050B]">
        <Clock className="h-3.5 w-3.5 shrink-0" />
        <span data-testid="ist-clock-date" className="text-[11px] font-extrabold uppercase tracking-wide sm:text-xs">{date}</span>
        <span className="opacity-50">·</span>
        <span data-testid="ist-clock-time" className="font-mono text-[11px] font-extrabold tabular-nums sm:text-xs">{time} IST</span>
      </div>
    </div>
  );
}

export function Header() {
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();

  const [theme, setTheme] = useState(
    () => (typeof document !== "undefined" && document.documentElement.dataset.theme) || "light"
  );
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    document.documentElement.dataset.theme = next;
    localStorage.setItem("bgr_theme", next);
  };

  const handleBrandTap = () => {
    if (location.pathname !== "/") navigate("/");
  };

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="relative mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
        <button type="button" onClick={handleBrandTap} data-testid="brand-logo" className="flex items-center gap-2">
          <img src={logo} alt={settings.business_name} className="h-14 w-14 shrink-0 object-contain drop-shadow-sm sm:h-16 sm:w-16" />
          <span className="flex flex-col text-left leading-none">
            <span className="font-display text-base font-extrabold tracking-tight text-[color:var(--text-primary)] sm:text-lg">
              {settings.business_name}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
              {settings.domain}
            </span>
          </span>
        </button>

        {/* Nav — only Home */}
        <nav className="flex items-center gap-1.5">
          <TranslateButton />
          <MemberChip />
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 rounded-full px-2.5 py-2 text-[15px] font-semibold transition-colors sm:px-4 ${
                  isActive
                    ? "bg-[color:var(--accent-maroon)] text-white"
                    : "text-[color:var(--text-secondary)] hover:text-[color:var(--accent-gold-bright)]"
                }`
              }
            >
              <item.icon className="h-4 w-4" /> <span className="hidden sm:inline">{item.label}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle light/dark theme"
            className="ml-1 grid h-10 w-10 place-items-center rounded-full border border-[color:var(--border-gold)] bg-[color:var(--bg-card)] text-[color:var(--accent-gold-bright)] transition-transform hover:scale-105 active:scale-95"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </nav>
      </div>
      <IstClock />
    </header>
  );
}
